import { useState, useEffect } from "react";
import { api, assetUrl, type Partner } from "@/lib/api";

const emptyForm = { name: "", logo_url: "", active: true };

export default function PartnersSettings() {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [uploading, setUploading] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      setPartners(await api.getPartners());
    } catch {
      setPartners([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    await api.createPartner(form);
    setForm(emptyForm);
    load();
  };

  const startEdit = (p: Partner) => {
    setEditingId(p.id);
    setEditForm({ name: p.name, logo_url: p.logo_url, active: p.active });
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingId) return;
    await api.updatePartner(editingId, editForm);
    setEditingId(null);
    load();
  };

  const handleDelete = async (id: string) => {
    if (!confirm("¿Eliminar este cliente/partner?")) return;
    await api.deletePartner(id);
    load();
  };

  const handleImageUpload = async (id: string, file: File) => {
    setUploading(id);
    try {
      await api.uploadPartnerLogo(id, file);
      load();
      if (editingId === id) {
        const updated = await api.getPartners();
        const p = updated.find((x) => x.id === id);
        if (p) setEditForm((f) => ({ ...f, logo_url: p.logo_url }));
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : "Error al subir imagen");
    }
    setUploading(null);
  };

  return (
    <div className="card space-y-6">
      <h2 className="font-semibold flex items-center gap-2">
        <i className="ri-building-4-line text-[var(--color-accent-oil)]"></i>
        Logos de Clientes (Confían en nosotros)
      </h2>

      <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1">Nombre</label>
          <input className="input-field" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        </div>
        <div>
          <label className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)] cursor-pointer pb-2">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            Activo
          </label>
        </div>
        <button type="submit" className="btn-primary">Añadir Logo</button>
      </form>

      {loading ? (
        <div className="text-center py-6 text-[var(--color-text-muted)]">
          <i className="ri-loader-4-line animate-spin text-2xl"></i>
        </div>
      ) : partners.length === 0 ? (
        <p className="text-[var(--color-text-muted)] text-sm text-center">No hay logos registrados.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {partners.map((p) => (
            <div key={p.id} className="p-4 rounded-xl bg-[var(--color-bg-tertiary)] border border-[var(--color-border)] flex gap-4 items-center">
              <div className="w-16 h-16 bg-white rounded-lg flex items-center justify-center overflow-hidden shrink-0">
                {p.logo_url ? (
                  <img src={assetUrl(p.logo_url)} alt={p.name} className="max-w-full max-h-full object-contain p-1" />
                ) : (
                  <i className="ri-image-line text-[var(--color-text-muted)] text-2xl"></i>
                )}
              </div>
              
              {editingId === p.id ? (
                <form onSubmit={handleUpdate} className="flex-1 space-y-2">
                  <input className="input-field py-1 text-sm" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required />
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input type="checkbox" checked={editForm.active} onChange={(e) => setEditForm({ ...editForm, active: e.target.checked })} />
                    Activo
                  </label>
                  <div className="flex gap-2">
                    <button type="submit" className="text-xs btn-primary px-2 py-1 flex-1">OK</button>
                    <button type="button" onClick={() => setEditingId(null)} className="text-xs btn-secondary px-2 py-1 flex-1">X</button>
                  </div>
                </form>
              ) : (
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-sm text-[var(--color-text-primary)] truncate">{p.name}</h3>
                  <p className="text-xs text-[var(--color-text-muted)]">{p.active ? "Activo" : "Oculto"}</p>
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => startEdit(p)} className="text-xs text-[var(--color-accent-oil)] hover:underline">Editar</button>
                    <label className="text-xs text-[var(--color-text-secondary)] hover:text-white cursor-pointer hover:underline">
                      {uploading === p.id ? "Subiendo..." : "Subir Logo"}
                      <input type="file" accept="image/*" className="hidden" disabled={uploading === p.id} onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleImageUpload(p.id, f);
                        e.target.value = "";
                      }} />
                    </label>
                    <button onClick={() => handleDelete(p.id)} className="text-xs text-red-400 hover:underline">Borrar</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
